import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { environment } from '@environments/environment';
import { tokenService } from '@core/auth/services/tokenService';
import { getSessionId } from '@shared/hooks/useSessionId';

const API_BASE_URL = `${environment.apiUrl}/api/v1`;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  // Send the httpOnly refresh-token cookie to same-site /auth/* requests.
  withCredentials: true,
});

// A bare client for the refresh call so it doesn't recurse through the
// interceptors below. withCredentials sends the refresh cookie.
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Call /auth/refresh relying on the httpOnly cookie; store the new access
// token in memory and return it. Throws if the cookie is missing/expired.
const performRefresh = async (): Promise<string> => {
  const response = await refreshClient.post('/auth/refresh', {});
  const accessToken: string = response.data.access_token;
  tokenService.setAccessToken(accessToken);
  return accessToken;
};

// State for proactive token refresh
let isProactiveRefreshing = false;

// Request interceptor: attach the in-memory access token, refreshing it
// proactively if it's about to expire.
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token logic for auth endpoints (they manage their own tokens)
    if (
      config.url?.includes('/auth/refresh') ||
      config.url?.includes('/auth/login')
    ) {
      return config;
    }

    const token = tokenService.getAccessToken();
    if (token) {
      // Proactively refresh if the access token expires within 5 minutes.
      if (tokenService.shouldRefreshToken() && !isProactiveRefreshing) {
        isProactiveRefreshing = true;
        try {
          const newToken = await performRefresh();
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch {
          // If proactive refresh fails, continue with the current token;
          // the response interceptor will handle any resulting 401.
          config.headers.Authorization = `Bearer ${token}`;
        } finally {
          isProactiveRefreshing = false;
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // No auth token — add the guest session ID for cart operations (if any).
      const sessionId = getSessionId();
      if (sessionId) {
        config.headers['X-Session-ID'] = sessionId;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// State for reactive (401) token refresh
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

const processQueue = (token: string | null) => {
  refreshQueue.forEach((callback) => {
    if (token) {
      callback(token);
    }
  });
  refreshQueue = [];
};

// Helper to check if request is an auth endpoint that shouldn't trigger redirects
const isAuthEndpoint = (url?: string): boolean => {
  if (!url) return false;
  const authEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/refresh',
    '/auth/verify-email',
    '/auth/password-reset',
    '/auth/resend-verification',
  ];
  return authEndpoints.some((endpoint) => url.includes(endpoint));
};

// Helper to check if request is a guest-compatible endpoint (shouldn't redirect to login)
const isGuestCompatibleEndpoint = (url?: string): boolean => {
  if (!url) return false;
  const guestEndpoints = ['/cart', '/products', '/categories', '/vendors'];
  return guestEndpoints.some((endpoint) => url.includes(endpoint));
};

const redirectToLogin = () => {
  tokenService.clearTokens();
  const returnUrl = window.location.pathname;
  window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
};

// Response interceptor: capture guest session ID + transparently refresh on 401.
apiClient.interceptors.response.use(
  (response) => {
    // Capture X-Session-ID from response headers (signed session from backend)
    const sessionId = response.headers['x-session-id'];
    if (sessionId && !tokenService.getAccessToken()) {
      // Only store for unauthenticated users
      localStorage.setItem('guest_session_id', sessionId);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Don't intercept auth endpoint errors — let them propagate to the caller.
    if (isAuthEndpoint(originalRequest?.url)) {
      return Promise.reject(error);
    }

    // On 401, try exactly one refresh (the refresh cookie may still be valid).
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the in-flight refresh to complete, then retry.
        return new Promise((resolve, reject) => {
          refreshQueue.push((token: string) => {
            if (!token) {
              reject(error);
              return;
            }
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await performRefresh();
        processQueue(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed: the cookie is missing/expired → the user isn't
        // logged in. Guest-compatible endpoints just get the error; anything
        // else bounces to login.
        processQueue(null);
        if (isGuestCompatibleEndpoint(originalRequest?.url)) {
          return Promise.reject(error);
        }
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
